import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { HashLink as Link } from "react-router-hash-link";
import { backendUrlImages } from "../../constant/config";

const SelectConferences = () => {
  const [allConferences, setAllConferences] = useState([]);
  const [conferenceName, setConferenceName] = useState("");
  const [selectedPriceId, setSelectedPriceId] = useState(null);

  // ✅ packages per conference
  const [groupPackages, setGroupPackages] = useState({});
  const [selectedGroupPackages, setSelectedGroupPackages] = useState({});

  // ✅ tab: "paid" | "free"
  const [activeGroupTab, setActiveGroupTab] = useState("paid");

  const navigate = useNavigate();
  const location = useLocation();
  const BaseUrl = process.env.REACT_APP_BASE_URL;

  const currentPath = location.pathname;
  const isAttendance = currentPath === "/registerPage/attendance";
  const isGroup = currentPath === "/registerPage/group";

  const getConference = () => {
    const searchQuery = conferenceName ? `?search=${encodeURIComponent(conferenceName)}` : "";
    const url = `${BaseUrl}/con/upcoming${searchQuery}`;

    axios
      .get(url)
      .then((response) => {
        const conferences = response.data?.upcoming_conferences || [];
        setAllConferences(conferences);
      })
      .catch((error) => {
        console.error("Error fetching conferences", error);
      });
  };

  const getGroupPackages = async (conferenceId) => {
    if (!isGroup) return;

    try {
      const res = await axios.get(
        `https://eventscons.com/eventbackendnew/api/group-packages/conference/${conferenceId}`,
        {
          headers: {
            Authorization: undefined,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      const list = Array.isArray(res.data?.data) ? res.data.data : [];

      setGroupPackages((prev) => ({
        ...prev,
        [conferenceId]: list,
      }));
    } catch (error) {
      console.error("Error fetching group packages:", conferenceId, error?.response?.data || error.message);
    }
  };

  useEffect(() => {
    getConference();
  }, [conferenceName]);

  useEffect(() => {
    if (!isGroup) return;
    if (!allConferences?.length) return;

    allConferences.forEach((conf) => {
      if (conf?.id && !groupPackages[conf.id]) {
        getGroupPackages(conf.id);
      }
    });
  }, [isGroup, allConferences]);

  // ✅ FIXED: Parse whats_include string to array
  const parseWhatsInclude = (whatsIncludeData) => {
    if (!whatsIncludeData) return [];
    
    try {
      // If already array
      if (Array.isArray(whatsIncludeData)) {
        return whatsIncludeData;
      }
      // If JSON string like "[\"Not include\"]"
      else if (typeof whatsIncludeData === 'string') {
        return JSON.parse(whatsIncludeData);
      }
    } catch (e) {
      console.warn("❌ whats_include parse error:", whatsIncludeData);
    }
    return [];
  };

  const handleAttendanceClick = (conference, price) => {
    setSelectedPriceId(price.id);

    // ✅ Parse whats_include before passing
    const whatsIncludeArray = parseWhatsInclude(price.whats_include);

    navigate(`/register/attendance/${conference.id}/${price.price_type}/${price.id}`, {
      state: {
        conferenceId: conference.id,
        conferenceTitle: conference.title,
        conferenceImage: conference.image,
        conferenceDate: conference.start_date,
        priceId: price.id,
        priceType: price.price_type,
        priceAmount: price.price,
        priceDescription: price.description,
        whatsInclude: whatsIncludeArray,  // ✅ Perfect array guaranteed!
      },
    });
  };

  const handleGroupCheckboxChange = (conferenceId, pkg, checked) => {
    setSelectedGroupPackages((prev) => {
      const current = prev[conferenceId] || [];
      let updated;

      if (checked) {
        const exists = current.some((p) => p.id === pkg.id);
        updated = exists ? current : [...current, pkg];
      } else {
        updated = current.filter((p) => p.id !== pkg.id);
      }

      return { ...prev, [conferenceId]: updated };
    });
  };

  const handleTitleClick = (conferenceId) => {
    if (isAttendance) return;

    // ✅ NEW: Add ?type=free if free tab is active
    const typeQuery = activeGroupTab === "free" ? `?type=free` : "";
    
    const paths = {
      "/registerPage/attendance": `/register/attendance/${conferenceId}${typeQuery}`,
      "/registerPage/speaker": `/register/speaker/${conferenceId}${typeQuery}`,
      "/registerPage/group": `/register/group/${conferenceId}${typeQuery}`,
      "/registerPage/sponsor": `/register/sponsor/${conferenceId}${typeQuery}`,
    };

    navigate(paths[currentPath] || currentPath, {
      state: {
        selectedGroupPrices: selectedGroupPackages[conferenceId] || [],
        activeGroupTab: activeGroupTab, // ✅ Pass tab state too
      },
    });
  };

  const isChecked = (conferenceId, pkgId) =>
    (selectedGroupPackages[conferenceId] || []).some((p) => p.id === pkgId);

  const getPackagesByTab = (conferenceId) => {
    const list = groupPackages[conferenceId] || [];
    if (activeGroupTab === "free") return list.filter((p) => p.type === "free");
    return list.filter((p) => p.type === "paid");
  };

  return (
    <>
      <section className="schedule-section" style={{ marginTop: "10vh" }}>
        <div className="auto-container">
          <div className="sec-title text-center">
            <span className="title">Available Conferences</span>
          </div>

          {/* ✅ Tabs only on Group page */}
          {isGroup && (
            <div style={{ display: "flex", justifyContent: "center", gap: 10, marginBottom: 20 }}>
              <button
                onClick={() => setActiveGroupTab("free")}
                style={{
                  padding: "10px 18px",
                  borderRadius: 8,
                  border: "1px solid #e5e7eb",
                  background: activeGroupTab === "free" ? "#111827" : "#fff",
                  color: activeGroupTab === "free" ? "#fff" : "#111827",
                  cursor: "pointer",
                  fontWeight: 400,
                }}
              >
                Free
              </button>
              <button
                onClick={() => setActiveGroupTab("paid")}
                style={{
                  padding: "10px 18px",
                  borderRadius: 8,
                  border: "1px solid #e5e7eb",
                  background: activeGroupTab === "paid" ? "#111827" : "#fff",
                  color: activeGroupTab === "paid" ? "#fff" : "#111827",
                  cursor: "pointer",
                  fontWeight: 400,
                }}
              >
                Paid
              </button>
            </div>
          )}

          <div className="schedule-tabs tabs-box">
            <div className="tabs-content">
              <div className="schedule-timeline">
                {allConferences?.map((conference, index) => (
                  <div
                    className={`schedule-block ${index % 2 === 0 ? "even" : "odd"}`}
                    key={conference.id}
                    style={{
                      display: "flex",
                      flexDirection: index % 2 === 0 ? "row" : "row-reverse",
                      marginBottom: "30px",
                    }}
                  >
                    <div className="inner-box" style={{ display: "flex", flexDirection: "column" }}>
                      <div className="inner">
                        <div className="date">
                          {new Date(conference.start_date).toLocaleDateString()}
                        </div>

                        <div className="speaker-info" style={{ marginBottom: "15px" }}>
                          <figure className="thumb" style={{ width: "60px", height: "60px" }}>
                            <img
                              style={{ width: "100%", height: "100%" }}
                              src={`${backendUrlImages}${conference.image}`}
                              alt={conference.title}
                            />
                          </figure>

                          <h5 className="name" style={{ fontSize: "18px", color: "#333", fontWeight: 400 }}>
                            {conference.title}
                          </h5>

                          {/* ✅ GROUP: show packages as per active tab */}
                          {isGroup && (
                            <div style={{ marginTop: 8, fontSize: 15, fontWeight: 400 }}>
                              <div style={{ marginBottom: 8, color: "#6b7280" }}>
                                Showing: {activeGroupTab === "free" ? "Free packages" : "Paid packages"}
                              </div>

                              {getPackagesByTab(conference.id).map((pkg) => (
                                <div key={pkg.id} style={{ marginBottom: 6 }}>
                                  <label style={{ fontWeight: 400 }}>
                                    <input
                                      type="checkbox"
                                      checked={isChecked(conference.id, pkg.id)}
                                      onChange={(e) =>
                                        handleGroupCheckboxChange(conference.id, pkg, e.target.checked)
                                      }
                                    />
                                    <span style={{ marginLeft: 8, fontWeight: 400 }}>
                                      {pkg.package_name}
                                      {activeGroupTab === "paid" && pkg.price_per_person
                                        ? ` - $${pkg.price_per_person} / person`
                                        : ""}
                                      {activeGroupTab === "free" ? " - Free" : ""}
                                    </span>
                                  </label>
                                </div>
                              ))}

                              {(groupPackages[conference.id] && getPackagesByTab(conference.id).length === 0) && (
                                <div style={{ color: "#9ca3af" }}>
                                  No {activeGroupTab} packages found
                                </div>
                              )}
                            </div>
                          )}

                          {/* ✅ FIXED: Attendance page prices with What's Include */}
                          {isAttendance && (
                            <ul className="prices-list" style={{ listStyleType: "none", padding: 0, margin: 0 }}>
                              {conference.prices?.map((price) => {
                                // ✅ Parse for display
                                const whatsIncludeItems = parseWhatsInclude(price.whats_include);
                                
                                return (
                                  <li
                                    key={price.id}
                                    className={`price-item ${selectedPriceId === price.id ? "selected" : ""}`}
                                    onClick={() => handleAttendanceClick(conference, price)}
                                    style={{
                                      backgroundColor: "#f8f8f8",
                                      padding: "15px",
                                      marginBottom: "10px",
                                      borderRadius: "8px",
                                      cursor: "pointer",
                                    }}
                                  >
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                                      <div style={{ fontWeight: 400, fontSize: 16, color: "#333" }}>
                                        {price.price_type}
                                      </div>
                                      <div style={{ fontSize: 18, fontWeight: 400, color: "#9B1321" }}>
                                        ${price.price}
                                      </div>
                                    </div>
                                    
                                    <div style={{ fontSize: 14, color: "#777", fontWeight: 400 }}>
                                      {price.description}
                                    </div>
                                    
                                    {/* ✅ PERFECT: What's Include Display */}
                                    {whatsIncludeItems.length > 0 && (
                                      <div style={{ 
                                        marginTop: 8, 
                                        padding: "8px 12px", 
                                        backgroundColor: "#f0f9ff", 
                                        borderRadius: "4px",
                                        fontSize: 13 
                                      }}>
                                        <strong style={{ color: "#0369a1", fontSize: 12 }}>What's Included:</strong>
                                        <div style={{ marginTop: 4 }}>
                                          {whatsIncludeItems.map((item, idx) => (
                                            <span 
                                              key={idx} 
                                              style={{ 
                                                display: "inline-block", 
                                                backgroundColor: "#e0f2fe", 
                                                padding: "2px 8px", 
                                                margin: "2px", 
                                                borderRadius: "12px", 
                                                fontSize: 12,
                                                color: "#0c4a6e"
                                              }}
                                            >
                                              {item}
                                            </span>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                        </div>

                        {/* Register button */}
                        {!isAttendance && (
                          <div className="btn-box" onClick={() => handleTitleClick(conference.id)} style={{ marginTop: "20px" }}>
                            <Link
                              className="theme-btn"
                              style={{
                                padding: "10px 20px",
                                borderRadius: "5px",
                                textAlign: "center",
                                display: "inline-block",
                                textDecoration: "none",
                              }}
                            >
                              Register
                            </Link>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default SelectConferences;